import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Input, App, Space, Select, Tag, Popover, Alert, List, Tabs } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined,
  UploadOutlined, DownloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';

const roleMap = { admin: '超管', manager: '管理员', owner: '业主', tenant: '租客', visitor: '访客' };
const roleColors = { admin: 'red', manager: 'orange', owner: 'green', tenant: 'blue', visitor: 'purple' };

const STAFF_ROLES = ['admin', 'manager'];
const RESIDENT_ROLES = ['owner', 'tenant', 'visitor'];
const STAFF_ROLE_OPTIONS = [
  { value: 'admin', label: '超管' },
  { value: 'manager', label: '管理员' }
];
const RESIDENT_ROLE_OPTIONS = [
  { value: 'owner', label: '业主' },
  { value: 'tenant', label: '租客' },
  { value: 'visitor', label: '访客' }
];

const LABEL_WIDTH = 80;
const FilterItem = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ display: 'inline-block', width: LABEL_WIDTH, textAlign: 'right', color: '#555', fontSize: 14, whiteSpace: 'nowrap' }}>{label}:</span>
    {children}
  </div>
);

const User = () => {
  const { message } = App.useApp();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const fixedCommunityId = !isSuperAdmin ? currentUser.communityId : null;

  const [activeTab, setActiveTab] = useState('resident');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const fileRef = useRef(null);

  const [roomModalVisible, setRoomModalVisible] = useState(false);
  const [roomModalUser, setRoomModalUser] = useState(null);
  const [userRooms, setUserRooms] = useState([]);
  const [relationType, setRelationType] = useState('owner');

  const [communities, setCommunities] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  const [filter, setFilter] = useState({ name: '', phone: '', role: '', communityId: fixedCommunityId });

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const tabRoleOptions = activeTab === 'staff' ? STAFF_ROLE_OPTIONS : RESIDENT_ROLE_OPTIONS;
  const tabTypes = activeTab === 'staff' ? STAFF_ROLES : RESIDENT_ROLES;
  const defaultRole = activeTab === 'staff' ? 'manager' : 'owner';

  useEffect(() => { fetchCommunities(); }, []);

  const fetchCommunities = async () => {
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setCommunities(res.data.list || []);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const buildQueryParams = (params = {}) => {
    const page = params.page ?? pagination.current;
    const pageSize = params.pageSize ?? pagination.pageSize;
    const merged = { page, pageSize };
    if (params.role !== undefined) {
      if (params.role) merged.type = params.role;
    } else if (filter.role) {
      merged.type = filter.role;
    } else if (params.types !== undefined) {
      merged.types = params.types;
    } else {
      merged.types = tabTypes.join(',');
    }
    if (params.name !== undefined) merged.name = params.name;
    else if (filter.name.trim()) merged.name = filter.name.trim();
    if (params.phone !== undefined) merged.phone = params.phone;
    else if (filter.phone.trim()) merged.phone = filter.phone.trim();
    if (!isSuperAdmin) {
      merged.communityId = fixedCommunityId;
    } else if (params.communityId !== undefined) {
      if (params.communityId) merged.communityId = params.communityId;
    } else if (filter.communityId) {
      merged.communityId = filter.communityId;
    }
    return merged;
  };

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const page = params.page ?? pagination.current;
      const pageSize = params.pageSize ?? pagination.pageSize;
      const merged = buildQueryParams(params);
      const res = await request.get('/user', { params: merged });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.pageSize || pageSize,
          total: res.data.total || 0
        });
      }
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setFilter({ name: '', phone: '', role: '', communityId: fixedCommunityId });
    setPagination(p => ({ ...p, current: 1 }));
    const types = key === 'staff' ? STAFF_ROLES : RESIDENT_ROLES;
    fetchData({ page: 1, pageSize: pagination.pageSize, types: types.join(',') });
  };

  const applyFilter = () => {
    setPagination(p => ({ ...p, current: 1 }));
    const params = { page: 1, pageSize: pagination.pageSize };
    if (filter.name.trim()) params.name = filter.name.trim();
    if (filter.phone.trim()) params.phone = filter.phone.trim();
    if (isSuperAdmin && filter.communityId) params.communityId = filter.communityId;
    if (filter.role) params.role = filter.role;
    fetchData(params);
  };

  const resetFilter = () => {
    setFilter({ name: '', phone: '', role: '', communityId: fixedCommunityId });
    setPagination(p => ({ ...p, current: 1 }));
    fetchData({ page: 1, pageSize: pagination.pageSize, types: tabTypes.join(',') });
  };

  const handleTableChange = (pag) => {
    setPagination(p => ({ ...p, current: pag.current, pageSize: pag.pageSize }));
    fetchData({ page: pag.current, pageSize: pag.pageSize });
  };

  const openRoomModal = async (record) => {
    setRoomModalUser(record);
    setSelectedCommunity(null);
    setSelectedBuilding(null);
    setSelectedUnit(null);
    setSelectedRoomId(null);
    setRelationType('owner');
    setUserRooms(record.rooms || []);
    setBuildings([]);
    setUnits([]);
    setRooms([]);
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setCommunities(res.data.list || []);
    } catch (error) { message.error('获取小区列表失败'); }
    if (fixedCommunityId) setSelectedCommunity(fixedCommunityId);
    setRoomModalVisible(true);
  };

  const addRoomRelation = async () => {
    if (!selectedRoomId) { message.warning('请选择房号'); return; }
    try {
      await request.post(`/room/${selectedRoomId}/user`, {
        user_id: roomModalUser.id,
        relation_type: relationType,
        is_primary: false
      });
      message.success('关联成功');
      const params = { page: pagination.current, pageSize: pagination.pageSize };
      if (isSuperAdmin && filter.communityId) params.communityId = filter.communityId;
      if (filter.role) params.role = filter.role;
      else params.types = tabTypes.join(',');
      const res = await request.get('/user', { params });
      if (res.code === 200) {
        const updated = res.data.list || [];
        setData(updated);
        setPagination(p => ({ ...p, total: res.data.total || 0 }));
        const u = updated.find(x => x.id === roomModalUser.id);
        if (u) setUserRooms(u.rooms || []);
      }
    } catch (error) {
      message.error('关联失败');
    }
  };

  useEffect(() => {
    if (!selectedCommunity) { setBuildings([]); setSelectedBuilding(null); return; }
    (async () => {
      const res = await request.get('/building', { params: { communityId: selectedCommunity, page: 1, pageSize: 999 } });
      if (res.code === 200) setBuildings(res.data.list || []);
    })();
  }, [selectedCommunity]);

  useEffect(() => {
    if (!selectedBuilding) { setUnits([]); setSelectedUnit(null); return; }
    (async () => {
      const res = await request.get('/unit', { params: { buildingId: selectedBuilding, page: 1, pageSize: 999 } });
      if (res.code === 200) setUnits(res.data.list || []);
    })();
  }, [selectedBuilding]);

  useEffect(() => {
    if (!selectedUnit) { setRooms([]); setSelectedRoomId(null); return; }
    (async () => {
      const res = await request.get('/room', { params: { unitId: selectedUnit, page: 1, pageSize: 999 } });
      if (res.code === 200) setRooms(res.data.list || []);
    })();
  }, [selectedUnit]);

  const removeRoomRelation = async (roomId) => {
    try {
      await request.delete(`/room/${roomId}/user/${roomModalUser.id}`);
      message.success('解绑成功');
      const params = { page: pagination.current, pageSize: pagination.pageSize };
      if (isSuperAdmin && filter.communityId) params.communityId = filter.communityId;
      if (filter.role) params.role = filter.role;
      else params.types = tabTypes.join(',');
      const res = await request.get('/user', { params });
      if (res.code === 200) {
        const updated = res.data.list || [];
        setData(updated);
        setPagination(p => ({ ...p, total: res.data.total || 0 }));
        const u = updated.find(x => x.id === roomModalUser.id);
        if (u) setUserRooms(u.rooms || []);
      }
    } catch (error) {
      message.error('解绑失败');
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ role: defaultRole, status: 'active' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone,
      role: record.role || defaultRole,
      status: record.status || 'active',
      communityId: record.communityId ?? null,
      password: ''
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定删除该用户？',
      onOk: async () => {
        await request.delete('/user/' + id);
        message.success('删除成功');
        fetchData();
      }
    });
  };

  const handleSubmit = async (values) => {
    const payload = { ...values };
    if (!payload.password) delete payload.password;
    if (activeTab === 'staff' && isSuperAdmin) {
      payload.communityId = values.communityId ?? null;
    }
    try {
      if (editingRecord) {
        await request.put('/user/' + editingRecord.id, payload);
        message.success('更新成功');
      } else {
        await request.post('/user', payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await request.get('/import/template', { responseType: 'blob' });
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_import_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error('下载模板失败');
    }
  };

  const handleFileSelect = () => { fileRef.current?.click(); };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      message.error('请上传 Excel 文件');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const uploadRes = await request.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (uploadRes.code !== 200) { message.error('上传失败'); return; }
      const previewRes = await request.post('/import/preview', { file_path: uploadRes.data.file_path });
      if (previewRes.code === 200) {
        setImportPreview(previewRes.data);
        setImportVisible(true);
      }
    } catch (error) { message.error('文件处理失败'); }
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.preview?.length) { message.warning('没有可导入的数据'); return; }
    setImporting(true);
    try {
      const res = await request.post('/import/execute', {
        data: importPreview.preview.map(r => ({
          phone: r.data['手机号'],
          name: r.data['姓名'],
          password: r.data['密码'],
          community_name: r.data['小区名称'],
          building_name: r.data['楼栋名称'],
          unit_name: r.data['单元名称'],
          room_number: r.data['房号'],
          relation_type: r.data['关系类型'],
          is_primary: r.data['是否主业主']
        }))
      });
      if (res.code === 200) {
        message.success(`导入完成：成功 ${res.data.success_count} 条，失败 ${res.data.fail_count} 条`);
        setImportVisible(false);
        setImportPreview(null);
        fetchData();
      }
    } catch (error) { message.error('导入失败'); }
    finally { setImporting(false); }
  };

  const handleExport = async () => {
    try {
      const res = await request.get('/export', { responseType: 'blob' });
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_export.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) { message.error('导出失败'); }
  };

  const renderRooms = (rooms) => {
    if (!rooms || rooms.length === 0) {
      return <Tag color="default">未关联</Tag>;
    }
    if (rooms.length === 1) {
      const r = rooms[0];
      return <span>{r.community_name || '-'} / {r.building_name || '-'} / {r.unit_name || '-'} / {r.number || '-'}</span>;
    }
    const content = (
      <div style={{ maxWidth: 300 }}>
        {rooms.map((r, i) => (
          <div key={i} style={{ padding: '4px 0', borderBottom: i < rooms.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            {r.community_name} / {r.building_name} / {r.unit_name} / {r.number}
            {r.is_primary ? <Tag color="orange" style={{ marginLeft: 8 }}>主</Tag> : null}
            <Tag style={{ marginLeft: 4 }}>{r.relation_type === 'owner' ? '业主' : '租客'}</Tag>
          </div>
        ))}
      </div>
    );
    return (
      <Popover content={content} title="关联房号">
        <Tag color="blue" style={{ cursor: 'pointer' }}>
          <HomeOutlined /> {rooms.length}套房
        </Tag>
      </Popover>
    );
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    ...(activeTab === 'resident' ? [{
      title: '关联房号', dataIndex: 'rooms', key: 'rooms', width: 280,
      render: (rooms) => renderRooms(rooms)
    }] : []),
    {
      title: '角色', dataIndex: 'role', key: 'role', width: 80,
      render: (text) => <Tag color={roleColors[text] || 'default'}>{roleMap[text] || text}</Tag>
    },
    ...(activeTab === 'staff' ? [{
      title: '所属小区', dataIndex: 'communityId', key: 'communityId', width: 140,
      render: (cid, record) => {
        if (record.isSuperAdmin) return <Tag color="red">全部小区</Tag>;
        const c = communities.find(x => x.id === cid);
        return c ? c.name : <span style={{ color: '#999' }}>未分配</span>;
      }
    }] : []),
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 70,
      render: (text) => text === 'active'
        ? <Tag color="success">正常</Tag>
        : <Tag color="error">禁用</Tag>
    },
    {
      title: '操作', key: 'action', width: activeTab === 'resident' ? 220 : 160,
      render: (_, record) => (
        <Space>
          {activeTab === 'resident' && (
            <Button type="link" size="small" icon={<HomeOutlined />} onClick={() => openRoomModal(record)}>关联房号</Button>
          )}
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <input type="file" ref={fileRef} style={{ display: 'none' }}
        accept=".xlsx,.xls" onChange={handleFileChange} />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
        items={[
          { key: 'resident', label: '住户 / 访客' },
          { key: 'staff', label: '管理人员' }
        ]}
      />

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {activeTab === 'staff' ? '添加管理人员' : '添加住户 / 访客'}
          </Button>
          {activeTab === 'resident' && (
            <>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>下载模板</Button>
              <Button icon={<UploadOutlined />} onClick={handleFileSelect}>批量导入</Button>
            </>
          )}
          <Button icon={<DownloadOutlined />} onClick={handleExport}>导出数据</Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <FilterItem label="小区">
            <Select
              placeholder={isSuperAdmin ? '全部小区' : '本小区'}
              value={filter.communityId || undefined}
              onChange={v => setFilter({ ...filter, communityId: v })}
              style={{ width: 180 }}
              disabled={!isSuperAdmin}
              allowClear={isSuperAdmin}
            >
              {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </FilterItem>
          <FilterItem label="姓名">
            <Input
              placeholder="输入姓名"
              value={filter.name}
              onChange={e => setFilter({ ...filter, name: e.target.value })}
              onPressEnter={applyFilter}
              style={{ width: 180 }}
              allowClear
            />
          </FilterItem>
          <FilterItem label="手机号">
            <Input
              placeholder="输入手机号"
              value={filter.phone}
              onChange={e => setFilter({ ...filter, phone: e.target.value })}
              onPressEnter={applyFilter}
              style={{ width: 180 }}
              allowClear
            />
          </FilterItem>
          <FilterItem label="角色">
            <Select
              placeholder="全部角色"
              value={filter.role || undefined}
              onChange={v => setFilter({ ...filter, role: v })}
              style={{ width: 180 }}
              allowClear
            >
              {tabRoleOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </FilterItem>
          <Button type="primary" onClick={applyFilter}>筛选</Button>
          <Button onClick={resetFilter}>重置</Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (t) => `共 ${t} 条`,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingRecord ? (activeTab === 'staff' ? '编辑管理人员' : '编辑住户/访客') : (activeTab === 'staff' ? '添加管理人员' : '添加住户/访客')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item label="手机号" name="phone" rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1\d{10}$/, message: '请输入正确的手机号' }
          ]}>
            <Input placeholder="请输入手机号" maxLength={11} />
          </Form.Item>
          <Form.Item label="密码" name="password" rules={[
            { required: !editingRecord, message: '请输入密码' },
            { min: 6, message: '密码至少6位' }
          ]}>
            <Input.Password placeholder={editingRecord ? '不填则保持原密码' : '请输入密码'} />
          </Form.Item>
          <Form.Item label="角色" name="role" rules={[{ required: true, message: '请选择角色' }]}>
            <Select>
              {tabRoleOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Form.Item>
          {activeTab === 'staff' && isSuperAdmin && (
            <Form.Item label="分配小区" name="communityId" extra="不选则不分配,登录后所有小区数据可见">
              <Select placeholder="未分配" allowClear>
                {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
              </Select>
            </Form.Item>
          )}
          <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
            <Select>
              <Select.Option value="active">正常</Select.Option>
              <Select.Option value="disabled">禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`关联房号 - ${roomModalUser?.name || ''}`}
        open={roomModalVisible}
        onCancel={() => setRoomModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>当前关联房号</div>
          {userRooms.length === 0 ? (
            <div style={{ color: '#999', padding: '8px 0' }}>暂未关联房号</div>
          ) : (
            <List
              size="small"
              dataSource={userRooms}
              renderItem={(r) => (
                <List.Item
                  actions={[
                    <Button type="link" size="small" danger onClick={() => removeRoomRelation(r.room_id)}>解绑</Button>
                  ]}
                >
                  {r.community_name} / {r.building_name} / {r.unit_name} / {r.number}
                  <Tag style={{ marginLeft: 8 }} color={r.relation_type === 'owner' ? 'green' : 'blue'}>
                    {r.relation_type === 'owner' ? '业主' : '租客'}
                  </Tag>
                </List.Item>
              )}
            />
          )}
        </div>
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>添加关联</div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="选择小区"
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              allowClear
              style={{ width: '100%' }}
            >
              {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
            <Select
              placeholder="选择楼栋"
              value={selectedBuilding}
              onChange={setSelectedBuilding}
              allowClear
              style={{ width: '100%' }}
              disabled={!selectedCommunity}
            >
              {buildings.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
            </Select>
            <Select
              placeholder="选择单元"
              value={selectedUnit}
              onChange={setSelectedUnit}
              allowClear
              style={{ width: '100%' }}
              disabled={!selectedBuilding}
            >
              {units.map(u => <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>)}
            </Select>
            <Select
              placeholder="选择房号"
              value={selectedRoomId}
              onChange={setSelectedRoomId}
              allowClear
              style={{ width: '100%' }}
              disabled={!selectedUnit}
            >
              {rooms.map(r => <Select.Option key={r.id} value={r.id}>{r.number}</Select.Option>)}
            </Select>
            <Select value={relationType} onChange={setRelationType} style={{ width: '100%' }}>
              <Select.Option value="owner">业主</Select.Option>
              <Select.Option value="tenant">租客</Select.Option>
            </Select>
            <Button type="primary" block icon={<PlusOutlined />} onClick={addRoomRelation}>添加关联</Button>
          </Space>
        </div>
      </Modal>

      <Modal
        title="导入预览"
        open={importVisible}
        onCancel={() => { setImportVisible(false); setImportPreview(null); }}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => { setImportVisible(false); setImportPreview(null); }}>取消</Button>,
          <Button key="import" type="primary" loading={importing}
            disabled={!importPreview?.preview?.length}
            icon={<UploadOutlined />}
            onClick={handleConfirmImport}>
            确认导入 ({importPreview?.preview?.length || 0} 条)
          </Button>
        ]}
      >
        {importPreview && (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
              <span style={{ color: '#52c41a' }}>
                <CheckCircleOutlined /> 有效数据: {importPreview.preview?.length || 0} 条
              </span>
              <span style={{ color: '#f5222d' }}>
                <CloseCircleOutlined /> 错误: {importPreview.errors?.length || 0} 条
              </span>
            </div>

            {importPreview.errors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Alert type="error" showIcon
                  message={`${importPreview.errors.length} 行数据存在错误，将跳过`}
                  description={
                    <List size="small" dataSource={importPreview.errors.slice(0, 10)}
                      renderItem={(err) => (
                        <List.Item>
                          第 {err.row} 行: {err.errors?.join('；')}
                        </List.Item>
                      )}
                    />
                  }
                />
              </div>
            )}

            {importPreview.preview?.length > 0 && (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <Table
                  size="small"
                  dataSource={importPreview.preview.slice(0, 20)}
                  rowKey="row"
                  pagination={false}
                  columns={[
                    { title: '行', dataIndex: 'row', width: 50 },
                    { title: '姓名', dataIndex: ['data', '姓名'], width: 80 },
                    { title: '手机号', dataIndex: ['data', '手机号'], width: 120 },
                    { title: '小区', dataIndex: ['data', '小区名称'], width: 100 },
                    { title: '楼栋', dataIndex: ['data', '楼栋名称'], width: 80 },
                    { title: '单元', dataIndex: ['data', '单元名称'], width: 80 },
                    { title: '房号', dataIndex: ['data', '房号'], width: 60 },
                    { title: '关系', dataIndex: ['data', '关系类型'], width: 60 }
                  ]}
                />
                {importPreview.preview.length > 20 && (
                  <div style={{ textAlign: 'center', padding: 8, color: '#999' }}>
                    仅显示前 20 条，共 {importPreview.preview.length} 条有效数据
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default User;
